<template>
  <div class="line-item">

    <div class="line-item-remove">
      <v-btn
        icon
        variant="outlined"
        class="line-item-remove-button"
        size="40"
        @click="removeLineItemFromOrder"
        @touchend="removeLineItemFromOrder"
      >
        <v-icon
          icon="mdi-close"
          color="#D7D7D7"
        />
      </v-btn>
    </div>

    <v-card
        class="rounded-lg line-item-style"
        variant="outlined"
        @click="selectLineItem"
        @touchend="selectLineItem"
    >
      <div class="d-flex flex-row align-center px-4 pa-2">
        <div class="flex-column flex-grow-1 min-width-0">

          <div class="item-title-solo d-flex align-center">
            <div class="title-text">
              {{ itemQuantity ? itemQuantity + ' × ' : '' }}{{ itemTitle }}
               <span v-if="hasDiscount" class="title-text">
                - {{ lineItem.discountPercent }}%
              </span>
            </div>
            <v-icon
              icon="mdi-square-edit-outline"
              size="32"
              class="ml-2 text-primary thin-icon"
              style="flex-shrink: 0"
            />
          </div>

          <div class="d-flex flex-row align-center" v-if="itemTimeSlot">
            <span style="color: #666666">{{itemTimeSlot}}</span>
          </div>

          <div class="item-price-solo">
            <!-- Show original price with strikethrough if there's a discount -->
            <div v-if="hasDiscount" class="price-with-discount">
              <div class="d-flex align-center gap-2">
                <span class="original-price" style="text-decoration: line-through;">
                  {{ formatPrice(itemPrice) }}
                </span>
                <span class="discounted-price" style="font-weight: bold;">
                  {{ formatPrice(discountedPrice) }}
                </span>
              </div>
              <span class="variation-text" style="color: #666666">{{ variationText }}</span>
            </div>
            <div v-else>
              <span class="variation-text" style="color: #666666">{{ formatPrice(itemPrice) }} {{ variationText }}</span>
            </div>
          </div>

        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { useStore } from "../../stores/store";

export default {
  name: "LineItem",
  props: {
    lineItem: {
      type: Object,
      default: null
    },
    index: {
      type: Number,
      default: -1
    }
  },
  setup() {
    const store = useStore()
    const { t, te } = useI18n()

    return {
      store,
      t,
      te
    }
  },
	data() {
		return {
			removingLineItemFromOrder: false,
			selectingLineItem: false
		}
	},
  computed: {
    itemImages() {
      if(typeof this.lineItem !== 'undefined' && this.lineItem !== null) {
        if(Array.isArray(this.lineItem.images)) {
          return this.lineItem.images
        }
      }
      return []
    },
    itemTimeSlot() {
      if (typeof this.lineItem !== 'undefined' && this.lineItem !== null && typeof this.lineItem.selectedTimeSlot !== 'undefined' && this.lineItem.selectedTimeSlot !== null) {
        try {
          const timeSlotInMillis = this.lineItem.selectedTimeSlot.timestampInMillis
          const date = new Date(timeSlotInMillis)
          const fullLocale = this.store.getCurrentLocale(this.$i18n.locale)
          
          const dayNum = date.getDate()
          const month = new Intl.DateTimeFormat(fullLocale, { month: 'short' }).format(date)
          const time = date.toLocaleTimeString(fullLocale, { hour: '2-digit', minute: '2-digit', hour12: false })
          
          return `${dayNum}. ${month} ${time}`
        } catch(e) {
          console.log("Error formatting time slot", e)
          return null
        }
      }
      return null
    },
    itemTitle() {
      return this.store.getItemTitle(this.lineItem)
    },
    itemQuantity() {
      if(typeof this.lineItem !== 'undefined' && this.lineItem !== null) {
        if (typeof this.lineItem.type !== 'undefined' && this.lineItem.type !== null && this.lineItem.type === 'event') {
          // Check if there are multiple variations selected.
          if(typeof this.lineItem.selectedVariations !== 'undefined' && this.lineItem.selectedVariations !== null && Array.isArray(this.lineItem.selectedVariations) && this.lineItem.selectedVariations.length > 1) {
            return this.lineItem.selectedVariations.length
          }
        }
        if(typeof this.lineItem.quantity !== 'undefined' && this.lineItem.quantity !== null && this.lineItem.quantity >= 0) {
          return this.lineItem.quantity
        }
      }
      return null
    },
    itemPrice() {
      if(typeof this.lineItem !== 'undefined' && this.lineItem !== null) {
        return this.store.getItemCurrentPrice(this.lineItem, this.lineItem.quantity, this.lineItem.selectedVariations)
      }
      return null
    },
    variationText() {
      if(typeof this.lineItem !== 'undefined' && this.lineItem !== null && 
         typeof this.lineItem.selectedVariations !== 'undefined' && 
         this.lineItem.selectedVariations !== null && 
         Array.isArray(this.lineItem.selectedVariations) && 
         this.lineItem.selectedVariations.length > 0) {
        
        // Get unique variation titles
        const uniqueTitles = [...new Set(this.lineItem.selectedVariations.map(v => v.label))];
        
        if (uniqueTitles.length === 0) return '';
        
        return `(${uniqueTitles.join(', ')})`;
      }
      return '';
    },
    hasDiscount() {
      return this.lineItem && this.lineItem.discountPercent > 0;
    },
    discountedPrice() {
      if (this.lineItem && this.lineItem.discountedTotalPrice) {
        return this.lineItem.discountedTotalPrice
      }
      return this.itemPrice;
    }
  },
  methods: {
    removeLineItemFromOrder() {
		if(this.removingLineItemFromOrder === true) {
			return
		}

		this.removingLineItemFromOrder = true
		setTimeout(() => {
			this.store.removeFromOrder(this.index)
			this.removingLineItemFromOrder = false
		}, 100)
    },
    selectLineItem() {
		if(this.selectingLineItem === true) {
			return
		}

		this.selectingLineItem = true
		setTimeout(() => {
			try {
				this.lineItem.editMode = true
				this.lineItem.index = this.index
				this.store.selectItem(JSON.parse(JSON.stringify(this.lineItem)))
			} catch (e) {
				this.store.customLogger('error', 'Could not stringify/parse line item to edit', e)
			}
			this.selectingLineItem = false
		}, 100)
    },
    formatPrice(price) {
      return this.store.formatPrice(price)
    },
    itemImage(size = 'large') {
      if(Array.isArray(this.itemImages) && this.itemImages.length > 0) {
        const firstImage = this.itemImages[0]
        if(typeof firstImage !== 'undefined' && firstImage !== null) {
          if(typeof firstImage.file !== 'undefined' && firstImage.file !== null) {
            if(typeof firstImage.file.sources !== 'undefined' && firstImage.file.sources !== null) {
              if(typeof firstImage.file.sources[size] !== 'undefined' && firstImage.file.sources[size] !== null) {
                return firstImage.file.sources[size]
              }
            }
          }
        }
      }
      return ''
    }
  }
}
</script>

<style scoped>
.min-width-0 {
  min-width: 0;
}

.title-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.item-title-solo {
  width: 100%;
  min-width: 0;
}

.variation-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.item-price-solo {
  max-width: 100%;
  overflow: hidden;
}

.price-with-discount {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.original-price {
  font-size: 14px;
}

.discount-badge {
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
}

.discounted-price {
  font-size: 16px;
  margin-top: 2px;
}

.gap-2 {
  gap: 8px;
}
</style>